import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useConsent } from "../context/ConsentContext";

export const ConsentBanner = () => {
  const { showBanner, settingsOpen, status, accept, reject, closeSettings } = useConsent();
  const isModal = settingsOpen; // opened from footer "Cookie Settings"
  const open = showBanner || settingsOpen;
  const acceptRef = useRef(null);
  const dialogRef = useRef(null);

  // Move focus into the surface when it opens; allow ESC to close the settings modal.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => acceptRef.current?.focus(), 30);
    const onKey = (e) => {
      if (e.key === "Escape" && isModal) closeSettings();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, isModal, closeSettings]);

  if (!open) return null;

  const description = (
    <p className="text-sm text-[#5A5A5A] leading-relaxed">
      We use <strong className="text-[#3D3D3D] font-medium">necessary</strong> technologies to run
      the site (security, checkout, downloads, and remembering this choice). With your permission we
      also use <strong className="text-[#3D3D3D] font-medium">optional analytics</strong> (Google
      Analytics and PostHog) to understand how the site is used. We never use advertising trackers.{" "}
      <Link
        to="/cookies"
        onClick={isModal ? closeSettings : undefined}
        className="text-[#B5715E] underline underline-offset-2 hover:text-[#9A5C4A]"
        data-testid="consent-cookie-policy-link"
      >
        Read our Cookie Policy
      </Link>
      .
    </p>
  );

  const buttons = (
    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 shrink-0">
      <button
        ref={acceptRef}
        type="button"
        onClick={accept}
        className="w-full sm:w-auto px-6 py-2.5 rounded-full bg-[#58B88B] text-white text-sm font-medium transition-colors hover:bg-[#4AA57A] focus:outline-none focus:ring-2 focus:ring-[#58B88B] focus:ring-offset-2"
        data-testid="consent-accept-button"
      >
        Accept Analytics
      </button>
      <button
        type="button"
        onClick={reject}
        className="w-full sm:w-auto px-6 py-2.5 rounded-full border border-[#C9C9C9] bg-white text-[#3D3D3D] text-sm font-medium transition-colors hover:bg-[#F2EFEC] focus:outline-none focus:ring-2 focus:ring-[#8A8A8A] focus:ring-offset-2"
        data-testid="consent-reject-button"
      >
        Reject Nonessential
      </button>
    </div>
  );

  if (isModal) {
    return (
      <div
        className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        data-testid="consent-settings-overlay"
      >
        <div
          className="absolute inset-0 bg-black/40"
          onClick={closeSettings}
          aria-hidden="true"
        />
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-settings-title"
          className="relative w-full max-w-lg rounded-2xl bg-[#FDF9F7] border border-[#E5E0DB] shadow-xl p-6 sm:p-8"
          data-testid="consent-settings-dialog"
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <h2
              id="cookie-settings-title"
              className="font-serif text-2xl text-[#3D3D3D]"
            >
              Cookie Settings
            </h2>
            <button
              type="button"
              onClick={closeSettings}
              aria-label="Close cookie settings"
              className="text-[#8A8A8A] hover:text-[#3D3D3D] transition-colors rounded focus:outline-none focus:ring-2 focus:ring-[#8A8A8A] text-xl leading-none"
              data-testid="consent-settings-close"
            >
              ×
            </button>
          </div>
          {status && (
            <p className="text-xs text-[#8A8A8A] mb-3" data-testid="consent-current-status">
              Current choice:{" "}
              <strong className="text-[#3D3D3D]">
                {status === "accepted" ? "Analytics allowed" : "Nonessential rejected"}
              </strong>
              . You can change or withdraw it below.
            </p>
          )}
          <div className="mb-6">{description}</div>
          {buttons}
        </div>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[110] bg-[#FDF9F7] border-t border-[#E5E0DB] shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
      data-testid="consent-banner"
    >
      <div className="max-w-5xl mx-auto px-5 py-5 sm:px-8 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
        <div className="flex-1 min-w-0">{description}</div>
        {buttons}
      </div>
    </div>
  );
};
